// src/App.tsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

// Providers
import { AuthProvider } from './context/AuthContext';

// Components
import PrivateRoute from './components/common/PrivateRoute';
import { Spinner } from './components/ui';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages - Auth (Lazy Loaded)
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const RegistroExitosoPage = lazy(() => import('./pages/auth/RegistroExitosoPage'));
const RegistroPendientePage = lazy(() => import('./pages/auth/RegistroPendientePage'));

// Pages - Distributor (Lazy Loaded)
const DistributorDashboardPage = lazy(() => import('./pages/distributor/DistributorDashboard'));
const DistributorCouponsPage = lazy(() => import('./pages/distributor/DistributorCouponsPage'));


// Pages - Public (Lazy Loaded)
const HomePage = lazy(() => import('./pages/public/HomePage'));
const PublicHistoryPage = lazy(() => import('./pages/public/PublicHistoryPage'));
const PublicWarrantyPage = lazy(() => import('./pages/public/PublicWarrantyPage'));

// Pages - Dashboard (Lazy Loaded)
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));

// Pages - SuperAdmin (Lazy Loaded)
const PlanManagementPage = lazy(() => import('./pages/superadmin/PlanManagementPage'));
const SuperAdminReportPage = lazy(() => import('./pages/admin/SuperAdminReportPage'));
const SuperAdminServicesPage = lazy(() => import('./pages/superadmin/SuperAdminServicesPage'));
const SuperAdminServiceDetailPage = lazy(() => import('./pages/superadmin/SuperAdminServiceDetailPage'));
const ManualRenewalDashboard = lazy(() => import('./pages/superadmin/ManualRenewalDashboard'));
const DistributorManagementPage = lazy(() => import('./pages/superadmin/DistributorManagementPage'));
// Handling named export for SuperAdminPaymentManager
const SuperAdminPaymentManager = lazy(() => import('./components/superadmin/SuperAdminPaymentManager').then(module => ({ default: module.SuperAdminPaymentManager })));

// Pages - Oil Changes (Lazy Loaded)
const OilChangeListPage = lazy(() => import('./pages/oilchanges/OilChangeListPage'));
const OilChangeFormPage = lazy(() => import('./pages/oilchanges/OilChangeFormPage'));
const OilChangeDetailPage = lazy(() => import('./pages/oilchanges/OilChangeDetailPage'));
const PendingOilChangesPage = lazy(() => import('./pages/oilchanges/PendingOilChangesPage'));
const CompleteOilChangePage = lazy(() => import('./pages/oilchanges/CompleteOilChangePage'));
const QuickOilChangeFormPage = lazy(() => import('./pages/oilchanges/QuickOilChangeFormPage'));

// Pages - Users (Lazy Loaded)
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const UserProfilePage = lazy(() => import('./pages/users/UserProfilePage'));
const ClientManagementPage = lazy(() => import('./pages/ClientManagementPage'));

// Pages - Settings (Lazy Loaded)
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));

// Pages - Reports (Lazy Loaded)
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const OperatorReportPage = lazy(() => import('./pages/reports/OperatorReportPage'));
const VehicleReportPage = lazy(() => import('./pages/reports/VehicleReportPage'));
const WarrantyReportsPage = lazy(() => import('./pages/reports/WarrantyReportsPage'));

// Pages - Services & Support (Lazy Loaded)
const UpcomingServicesPage = lazy(() => import('./pages/services/UpcomingServicesPage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));

// Pages - Admin Lubricentros (Lazy Loaded)
const LubricentroFormPage = lazy(() => import('./pages/admin/LubricentroFormPage'));
const LubricentroDetailPage = lazy(() => import('./pages/admin/LubricentroDetailPage'));
const LubricentroSubscriptionPage = lazy(() => import('./pages/admin/LubricentroSubscriptionPage'));
const LubricentroManagementPage = lazy(() => import('./pages/superadmin/LubricentroManagementPage'));
const SuperAdminCouponsPage = lazy(() => import('./pages/admin/SuperAdminCouponsPage'));
const PaymentsDashboardPage = lazy(() => import('./pages/superadmin/PaymentsDashboardPage'));
const ServiceLookupPage = lazy(() => import('./pages/public/ServiceLookupPage'));

// Pages - Garantías (Lazy Loaded)
const WarrantyDashboardPage = lazy(() => import('./pages/warranties/WarrantyDashboardPage'));
const WarrantyFormPage = lazy(() => import('./pages/warranties/WarrantyFormPage'));
const WarrantyDetailPage = lazy(() => import('./pages/warranties/WarrantyDetailPage'));

// Pages - Payment Management (Lazy Loaded)
const PaymentManagementPage = lazy(() => import('./pages/admin/PaymentManagementPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

// Pages - Payment Results (Lazy Loaded)
const PaymentSuccessPage = lazy(() => import('./pages/payment/PaymentSuccessPage'));
const PaymentPendingPage = lazy(() => import('./pages/payment/PaymentPendingPage'));
const PaymentFailurePage = lazy(() => import('./pages/payment/PaymentFailurePage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
    },
  },
});

// Loading Fallback Component
const PageLoader = () => (
  <div className="flex justify-center items-center h-screen">
    <Spinner size="lg" />
  </div>
);

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/consulta-historial" element={<PublicHistoryPage />} />
                <Route path="/consulta-garantia/:id" element={<PublicWarrantyPage />} />
                <Route path="/lubri/:lubricentroId" element={<ServiceLookupPage />} />

                {/* Rutas de resultado de pagos */}
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/failure" element={<PaymentFailurePage />} />
                <Route path="/payment/pending" element={<PaymentPendingPage />} />

                {/* ==================== RUTAS DE AUTENTICACIÓN ==================== */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                </Route>

                {/* Rutas de registro */}
                <Route path="/registro-exitoso" element={<RegistroExitosoPage />} />
                <Route path="/registro-pendiente" element={<RegistroPendientePage />} />

                {/* ==================== RUTAS PROTEGIDAS ==================== */}
                <Route element={<MainLayout />}>
                  {/* ==================== DASHBOARD PRINCIPAL ==================== */}
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute>
                        <DashboardPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== GESTIÓN DE PAGOS ==================== */}
                  <Route
                    path="/admin/pagos"
                    element={
                      <PrivateRoute requiredRoles={['admin']}>
                        <PaymentManagementPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Gestión de Cupones de Descuento */}
                  <Route
                    path="/superadmin/cupones"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <SuperAdminCouponsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Gestión de Transferencias para SuperAdmin */}
                  <Route
                    path="/superadmin/pagos-transferencias"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <SuperAdminPaymentManager />
                      </PrivateRoute>
                    }
                  />

                  {/* Dashboard General de Pagos (Agregado) */}
                  <Route
                    path="/superadmin/pagos"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <PaymentsDashboardPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE SUPERADMIN ==================== */}
                  <Route
                    path="/superadmin/planes"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <PlanManagementPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/renovaciones"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <ManualRenewalDashboard />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/reportes"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <SuperAdminReportPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/servicios"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <SuperAdminServicesPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/servicios/:serviceId"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <SuperAdminServiceDetailPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/lubricentros/:lubricentroId/servicios/:serviceId/editar"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <OilChangeFormPage />
                      </PrivateRoute>
                    }
                  />

                  {/* Gestión de lubricentros */}
                  <Route
                    path="/superadmin/lubricentros"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <LubricentroManagementPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/distribuidores"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <DistributorManagementPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/lubricentros/nuevo"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <LubricentroFormPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/lubricentros/editar/:id"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <LubricentroFormPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/lubricentros/:id"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <LubricentroDetailPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/superadmin/lubricentros/suscripcion/:id"
                    element={
                      <PrivateRoute requiredRoles={['superadmin']}>
                        <LubricentroSubscriptionPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE CAMBIOS DE ACEITE ==================== */}
                  <Route
                    path="/cambios-aceite"
                    element={
                      <PrivateRoute>
                        <OilChangeListPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/nuevo"
                    element={
                      <PrivateRoute requiresActiveSubscription={true}>
                        <OilChangeFormPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/:id"
                    element={
                      <PrivateRoute>
                        <OilChangeDetailPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/editar/:id"
                    element={
                      <PrivateRoute requiresActiveSubscription={true}>
                        <OilChangeFormPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/pendientes"
                    element={
                      <PrivateRoute>
                        <PendingOilChangesPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/completar/:id"
                    element={
                      <PrivateRoute requiresActiveSubscription={true}>
                        <CompleteOilChangePage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/cambios-aceite/precarga"
                    element={
                      <PrivateRoute requiresActiveSubscription={true}>
                        <QuickOilChangeFormPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE GARANTÍAS ==================== */}
                  <Route
                    path="/garantias"
                    element={
                      <PrivateRoute requiredPermission="garantias">
                        <WarrantyDashboardPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/garantias/nueva"
                    element={
                      <PrivateRoute requiredPermission="garantias" requiresActiveSubscription={true}>
                        <WarrantyFormPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/garantias/:id"
                    element={
                      <PrivateRoute requiredPermission="garantias">
                        <WarrantyDetailPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/garantias/editar/:id"
                    element={
                      <PrivateRoute requiredPermission="garantias" requiresActiveSubscription={true}>
                        <WarrantyFormPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE USUARIOS ==================== */}
                  <Route
                    path="/usuarios"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <UserListPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/clientes"
                    element={
                      <PrivateRoute requiredPermission="clientes">
                        <ClientManagementPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/perfil"
                    element={
                      <PrivateRoute>
                        <UserProfilePage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE REPORTES ==================== */}
                  <Route
                    path="/reportes"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <ReportsPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/reportes/garantias"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <WarrantyReportsPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/reportes/operador/:id"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <OperatorReportPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/reportes/vehiculo/:dominio"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <VehicleReportPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE SERVICIOS Y SOPORTE ==================== */}
                  <Route
                    path="/proximos-servicios"
                    element={
                      <PrivateRoute>
                        <UpcomingServicesPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/soporte"
                    element={
                      <PrivateRoute>
                        <SupportPage />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/notificaciones"
                    element={
                      <PrivateRoute>
                        <NotificationsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== RUTAS DE DISTRIBUIDOR ==================== */}
                  <Route
                    path="/distributor/dashboard"
                    element={
                      <PrivateRoute requiredRoles={['distributor']}>
                        <DistributorDashboardPage />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/distributor/cupones"
                    element={
                      <PrivateRoute requiredRoles={['distributor']}>
                        <DistributorCouponsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ==================== CONFIGURACIÓN ==================== */}
                  <Route
                    path="/settings/*"
                    element={
                      <PrivateRoute requiredRoles={['admin', 'superadmin']}>
                        <SettingsPage />
                      </PrivateRoute>
                    }
                  />
                </Route>

                {/* ==================== REDIRECCIONES ==================== */}
                {/* Redirigir rutas desconocidas */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;